import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { MessageContact } from './models';
import { baseApi } from '@centerpiece/components';

export const message_contact = baseApi
  .enhanceEndpoints({ addTagTypes: ['MessageContacts', 'MessageContact'] })
  .injectEndpoints({
    endpoints: builder => ({
      getMessageContacts: builder.query<MessageContact[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: `message_contact`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'MessageContact' as const,
                  id,
                })),
                'MessageContacts',
              ]
            : ['MessageContacts'],
      }),
      getMessageContact: builder.query<
        MessageContact,
        { id: number; params?: object }
      >({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `message_contact/${id}`,
          method: 'GET',
          params: params ? { rollup: 'all', ...params } : { rollup: 'all' },
        }),
        providesTags: (result, error, arg) => [
          { type: 'MessageContact', id: arg.id },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createMessageContact: builder.mutation({
        query: request => ({
          url: 'message_contact',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['MessageContacts'],
      }),
      patchMessageContact: builder.mutation<MessageContact, MessageContact>({
        query: request => ({
          url: `message_contact/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'MessageContact', id: arg.id },
        ],
      }),
      updateMessageContact: builder.mutation<MessageContact, MessageContact>({
        query: request => ({
          url: `message_contact/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'MessageContact', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            message_contact.util.updateQueryData(
              'getMessageContact',
              { id: id },
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(message_contact.util.invalidateTags(['MessageContacts']))
             */
            dispatch(message_contact.util.invalidateTags(['MessageContacts']));
          }
        },
      }),
      deleteMessageContact: builder.mutation<MessageContact, number>({
        query: id => ({
          url: `message_contact/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'MessageContact' as const, arg }, 'MessageContacts']
            : ['MessageContacts'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchMessageContactMutation,
  useGetMessageContactsQuery,
  useGetMessageContactQuery,
  useCreateMessageContactMutation,
  useUpdateMessageContactMutation,
  useDeleteMessageContactMutation,
  usePrefetch: useMessageContactsPrefetch,
} = message_contact;
const selectMessageContactsRoot =
  message_contact.endpoints.getMessageContacts.select({});
export const selectMessageContacts = createSelector(
  selectMessageContactsRoot,
  message_contact => message_contact.data || []
);
